import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DomainFavicon from '@/app/common/components/DomainFavicon';
import type { CompetitorInsight } from '../../types';
import { CompetitorFields, type useCompetitorForm } from '../CompetitorForm';

type CompetitorForm = ReturnType<typeof useCompetitorForm>;

type SetupCompetitorStepProps = {
  competitors: CompetitorInsight[];
  form: CompetitorForm;
  saving: boolean;
  deletingCompetitorId: string | null;
  onSubmit: () => void;
  onDelete: (competitorId: string) => void;
};

export function SetupCompetitorStep({
  competitors,
  form,
  saving,
  deletingCompetitorId,
  onSubmit,
  onDelete,
}: SetupCompetitorStepProps) {
  const saveLabel = saving ? 'در حال افزودن…' : 'افزودن رقیب';

  return (
    <div className="d-flex flex-column gap-3">
      <p className="fs--1 text-500 lh-base mb-0">
        یک یا چند رقیب اضافه کنید تا دیده‌شدن برندتان با آن‌ها مقایسه شود.
      </p>

      {competitors.length > 0 && (
        <div className="d-flex flex-column gap-2">
          <div className="fs--1 fw-medium text-700">
            رقبای اضافه‌شده ({competitors.length})
          </div>
          <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
            {competitors.map((competitor) => {
              const deleting = deletingCompetitorId === competitor.id;
              return (
                <li
                  key={competitor.id}
                  className="d-flex align-items-center gap-2 fs--1 px-3 py-2 rounded-3 border bg-100 dark__bg-1100"
                >
                  <DomainFavicon domain={competitor.host} />
                  <span className="fw-medium text-800 text-truncate">
                    {competitor.brandName}
                  </span>
                  <span className="text-truncate fs--2 text-500 ms-auto" dir="ltr">
                    {competitor.host}
                  </span>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="p-0 text-500 flex-shrink-0"
                    disabled={deleting || saving}
                    onClick={() => onDelete(competitor.id)}
                    aria-label={`حذف ${competitor.brandName}`}
                    title="حذف رقیب"
                  >
                    <FontAwesomeIcon
                      icon={deleting ? 'spinner' : 'trash-alt'}
                      className={deleting ? 'fs--2 fa-spin' : 'fs--2'}
                    />
                  </Button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="rounded-3 border bg-100 dark__bg-1100 p-3 d-flex flex-column gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary flex-shrink-0"
            style={{ width: 28, height: 28 }}
          >
            <FontAwesomeIcon icon="plus" className="fs--2" />
          </span>
          <div className="min-w-0">
            <div className="fs--1 fw-medium text-800 mb-0">
              {competitors.length > 0 ? 'رقیب بعدی' : 'رقیب جدید'}
            </div>
            <div className="fs--2 text-500">
              فیلدها را پر کنید و با دکمه زیر، به فهرست رقبا اضافه کنید.
            </div>
          </div>
        </div>

        <CompetitorFields
          form={form}
          formId="setup-competitor-form"
          autoFocus={competitors.length === 0}
          compact
          onSubmit={() => void onSubmit()}
        />

        <div className="d-flex align-items-center justify-content-between gap-2 pt-1 border-top">
          <span className="fs--2 text-500">
            می‌توانید چند رقیب پشت‌سرهم اضافه کنید
          </span>
          <Button
            type="submit"
            form="setup-competitor-form"
            variant="outline-primary"
            size="sm"
            disabled={saving || !!deletingCompetitorId || !form.canSubmit}          >
            {!saving && <FontAwesomeIcon icon="plus" />}
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
